import React, { useCallback, useEffect, useMemo, useRef } from "react";
import PropTypes from "prop-types";
import { AsyncTypeahead, Typeahead, Hint } from "react-bootstrap-typeahead";
import Form from "react-bootstrap/Form";
import classNames from "classnames";
import { InputPropTypes } from "../../../propTypes";
import InputGroup from "../../InputGroup";
import ClearIcon from "../../../Icon/Clear";

import "./styles.scss";

// Bypassing default FE filtering
const filterBy = () => true;

export const Autocomplete = ({
  label,
  /* Props to be applied directly to the input. onBlur, onChange, onFocus, and onKeyDown are ignored. */
  input: { onFocus, onBlur, onChange, onKeyDown, ...input },
  disabled,
  readonly,
  typeaheadRef,
  inputRef,
  id,
  isLoading,
  minLength,
  delay,
  onSearch,
  onInputChange,
  options,
  onSelectionChange,
  onMenuToggle,
  paginationText,
  pageSize = 15,
  onPaginate,
  labelKey,
  optionLabelMapper,
  maxLength,
  withoutHint,
  rounded,
  type,
  name,
  async,
  meta,
}) => {
  const autocompleteRef = useRef(null);

  useEffect(() => {
    if (inputRef) {
      if (autocompleteRef.current) {
        // set only native input ref on top of general interface
        inputRef.current = autocompleteRef.current.getInput();
        return;
      }
      inputRef.current = autocompleteRef.current;
    }
  }, [autocompleteRef.current]);
  // Autocomplete keeps "value" in internal state
  // When value become empty in props - Autocomplete does not set it in internal state
  useEffect(() => {
    if (!input.value && autocompleteRef.current?.state.text) {
      autocompleteRef.current.clear();
    } else if (autocompleteRef.current) {
      const updateValues =
        meta?.active && !disabled
          ? { text: input.value, selected: [] }
          : {
              text: input.value,
              selected: [],
              showMenu: false,
              isFocused: false,
            };
      autocompleteRef.current.setState(updateValues);
    }
  }, [input.value]);

  const renderInput = useCallback(
    ({ inputRef, referenceElementRef, ...inputProps }) => {
      const WrapperComponent = withoutHint ? React.Fragment : Hint;
      return (
        <WrapperComponent>
          <Form.Control
            {...inputProps}
            onBlur={onBlur}
            onFocus={onFocus}
            className="rbt-input-main"
            // Workaround to disable default form autocomplete
            autoComplete="new-password"
            maxLength={maxLength}
            ref={node => {
              inputRef(node);
              referenceElementRef(node);
            }}
            name={name}
            disabled={disabled || readonly}
          />
        </WrapperComponent>
      );
    },
    [withoutHint, maxLength, disabled, readonly, name, onBlur, onFocus]
  );

  const onClearClick = useCallback(() => {
    if (onInputChange) {
      onInputChange("");
    }
  }, [onInputChange]);

  const append = useMemo(() => {
    return (
      <div onClick={onClearClick} className="icon">
        <ClearIcon />
      </div>
    );
  }, [onClearClick]);

  const isClearActionAvailable = useMemo(
    () => input.value && !disabled && !readonly && type === "search",
    [input.value, disabled, readonly, type]
  );

  const handleSelectionChange = useCallback(
    async selection => {
      if (selection[0]) {
        await onSelectionChange(selection[0]);

        autocompleteRef.current?.blur();
      }
    },
    [onSelectionChange]
  );

  const TypeaheadComponent = async ? AsyncTypeahead : Typeahead;

  return (
    <InputGroup
      className={classNames(
        "mydpd-search-autocomplete",
        rounded && "rounded",
        isClearActionAvailable && "with-clear-button"
      )}
      append={isClearActionAvailable ? append : null}
    >
      <TypeaheadComponent
        ref={node => {
          [autocompleteRef, typeaheadRef].forEach(ref => {
            if (typeof ref === "function") {
              ref(node);
            } else if (typeof ref === "object") {
              if (!ref.hasOwnProperty("current")) {
                throw new Error("Unexpected ref object provided");
              }
              ref.current = node;
            }
          });
        }}
        filterBy={async ? filterBy : []}
        id={id}
        isLoading={async ? isLoading : null}
        minLength={async ? minLength : null}
        delay={async ? delay : null}
        onSearch={async ? onSearch : null}
        onInputChange={onInputChange}
        options={options}
        onKeyDown={onKeyDown}
        onChange={handleSelectionChange}
        onMenuToggle={onMenuToggle}
        paginationText={paginationText}
        maxResults={pageSize}
        onPaginate={onPaginate}
        labelKey={labelKey}
        disabled={disabled || readonly}
        useCache={false}
        renderMenuItemChildren={optionLabelMapper}
        renderInput={renderInput}
        inputProps={{ id, ...input }}
      />
    </InputGroup>
  );
};

Autocomplete.propTypes = {
  id: PropTypes.string.isRequired,
  pageSize: PropTypes.number,
  typeaheadRef: PropTypes.any,
  isLoading: PropTypes.bool,
  withoutHint: PropTypes.bool,
  minLength: PropTypes.number,
  delay: PropTypes.number,
  onSearch: PropTypes.func,
  onInputChange: PropTypes.func,
  options: PropTypes.array,
  onSelectionChange: PropTypes.func,
  onMenuToggle: PropTypes.func,
  paginationText: PropTypes.string,
  onPaginate: PropTypes.func,
  labelKey: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  optionLabelMapper: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  maxLength: PropTypes.number,
  async: PropTypes.bool,
  ...InputPropTypes,
};

Autocomplete.defaultProps = {
  async: true,
};
